import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  createFilterOptions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import {blue} from '@mui/material/colors';
import {get, isArray, isNull, isObject, isString} from 'lodash';
import {
  FC,
  KeyboardEventHandler,
  memo,
  ReactNode,
  SyntheticEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {RecoilState, SetterOrUpdater, useRecoilState, useRecoilValue} from 'recoil';
import {FilterService} from '../../../api/services/filters.service';
import {ISelectOption} from '../../../api/strategies/option.type';
import {useSearchStrategy} from '../../../hooks/useSearchStrategy';
import {EventBusContext} from '../../../providers/EventBus';
import {FilterNameType, FilterQueryType, FilterType, IFiltersSet} from '../../../shared/types/filtersSet.types';
import {authState} from '../../../store/auth/atoms';
import {ModalContainer} from '../../modals/ModalContainer';
import {isPrevSelectedKey} from '../guards/isPrevSelectedKey';
import {usePrevSelectedStore} from '../store/prevSelectedStore';
import {FilterItem} from './FilterItem';
import {AccordionHandle, FiltersAccordion} from './FiltersAccordion';
import {FiltersSetItem} from './FiltersSetItem';
import {SaveSetModal} from './SaveSetModal';

interface IFilterForm {
  filterBy: FilterNameType | string;
  value: string | Record<string, any>;
}

type DictSearchType = {
  [key: string]: {
    searchStrategy: (arg: string) => Promise<unknown[]>;
  };
};

interface IFilters {
  disableMemo?: boolean;
  isLoading: boolean;
  filterType: FilterQueryType;
  filtersState: RecoilState<FilterType[]>;
  filtersSetsState: RecoilState<IFiltersSet[]>;
  currentFiltersSetState: RecoilState<IFiltersSet | null>;
  triggerQuery: SetterOrUpdater<number>;
  filterBy: string[][];
  predefined?: Record<string, string[][] | Error | Promise<unknown>>;
  dictSearch?: DictSearchType;
  dicts?: Record<string, Map<string, string>>;
  resetPageable?: () => void;
}

const filter = createFilterOptions<{title: string; value: string}>();

const buttonStyle = {
  fontSize: '16px',
  textTransform: 'none',
};

const Filters: FC<IFilters> = ({
  filtersState,
  filtersSetsState,
  currentFiltersSetState,
  predefined,
  dicts,
  filterBy,
  dictSearch,
  resetPageable,
  triggerQuery,
  filterType,
  isLoading,
  disableMemo,
}) => {
  const {
    control,
    setValue,
    watch,
    handleSubmit,
    getValues,
    formState: {isValid},
  } = useForm<IFilterForm>({
    mode: 'onChange',
    defaultValues: {
      value: '',
      filterBy: '',
    },
  });

  const panel1Ref = useRef<AccordionHandle | null>(null);
  const panel2Ref = useRef<AccordionHandle | null>(null);
  const {data: prevSelectedData, setData: setPrevSelectedData} = usePrevSelectedStore();

  const [filters, setFilters] = useRecoilState(filtersState);
  const [filtersSets, setFiltersSets] = useRecoilState(filtersSetsState);
  const [currFiltersSet, setCurrFiltersSet] = useRecoilState(currentFiltersSetState);
  const {user} = useRecoilValue(authState);
  const [openModal, setOpenModal] = useState(false);
  const [openSelect, setOpenSelect] = useState(false);
  const {bus} = useContext(EventBusContext);

  const filterByWatch = watch('filterBy');

  const {handleSearch, data, searchLoading, searchTerm, resetSearch} = useSearchStrategy<unknown>(
    dictSearch && dictSearch[filterByWatch] ? dictSearch[filterByWatch].searchStrategy : null,
  );

  const toggleOpenModal = () => {
    setOpenModal(!openModal);
  };

  const handleExpandFilter = (_: SyntheticEvent, expand: boolean) => () => {
    if (!expand) return;
    setValue('filterBy', '', {shouldValidate: true});
    setValue('value', '', {shouldValidate: true});
  };

  const cancelFilter = () => {
    setValue('filterBy', '', {shouldValidate: true});
    setValue('value', '', {shouldValidate: true});
    panel1Ref.current?.close();
    resetPageable?.();
  };

  const resetValue = () => {
    setValue('value', '', {shouldValidate: true});
    resetSearch();
  };

  const addFilter: SubmitHandler<IFilterForm> = (data) => {
    setFilters((prev) => [
      ...prev,
      [data.filterBy as FilterNameType, get(data.value, 'value.name', get(data.value, 'value', data.value))],
    ]);
    setValue('filterBy', '', {shouldValidate: true});
    setValue('value', '', {shouldValidate: true});
    panel1Ref.current?.close();
    if (isPrevSelectedKey(data.filterBy) && isString(data.value)) {
      setPrevSelectedData(data.filterBy, data.value);
    }
    resetPageable?.();
  };

  const resetFilters = () => {
    if (filters.length) {
      setFilters([]);
      setCurrFiltersSet(null);
      resetPageable?.();
    }
  };

  const keyHandler: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.code === 'Escape') {
      setValue('value', '', {shouldValidate: true});
    }

    if (e.code === 'Enter' && getValues('value')) {
      handleSubmit(addFilter)();
    }
  };

  const editFiltersSet = async (set: IFiltersSet) => {
    await FilterService.update({
      ...set,
      filters: JSON.stringify(set.filters),
    });
    setCurrFiltersSet(set);
    setFiltersSets((prev) => prev.map((i) => (i.id === set.id ? set : i)));
  };

  const saveFiltersSet = async (set: IFiltersSet) => {
    await FilterService.create({
      ...set,
      filterType,
      filters: JSON.stringify(set.filters),
      userId: user?.id as number,
    });

    triggerQuery((v) => v + 1);
  };

  const handleSetStrategy =
    (setModalLoading: (loading: boolean) => void) => async (effect: 'edit' | 'save', set: IFiltersSet) => {
      try {
        setModalLoading(true);

        switch (effect) {
          case 'edit':
            await editFiltersSet(set);

            toast.success('Набор фильтров изменен', {
              containerId: 'default',
              autoClose: 2000,
              hideProgressBar: true,
              pauseOnHover: false,
              draggable: false,
            });

            break;

          case 'save':
            await saveFiltersSet(set);

            toast.success('Набор фильтров создан', {
              containerId: 'default',
              autoClose: 2000,
              hideProgressBar: true,
              pauseOnHover: false,
              draggable: false,
            });

            break;
        }
        setValue('filterBy', '', {shouldValidate: true});
        setValue('value', '', {shouldValidate: true});

        toggleOpenModal();
      } catch (err) {
        bus?.emit('error', [err]);
      } finally {
        setModalLoading(false);
      }
    };

  useEffect(() => {
    if (filters.length === 0 && currFiltersSet) {
      setCurrFiltersSet(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.length]);

  return (
    <>
      <Box width="100%">
        <Stack direction="column" spacing={2}>
          <Stack
            direction="row"
            sx={{justifyContent: 'space-between', height: '32px', position: 'relative', zIndex: 5}}
          >
            <Stack position="absolute" direction="row" spacing={2}>
              <Box>
                <FiltersAccordion
                  ref={panel1Ref}
                  onChange={handleExpandFilter}
                  summaryIcon={<AddIcon sx={{mr: 1}} fontSize="small" />}
                  summaryContent="Новый фильтр"
                >
                  <Controller
                    control={control}
                    name="filterBy"
                    render={({field: {onChange, value}}) => (
                      <FormControl sx={{mb: 1}} fullWidth>
                        <InputLabel size="small" id="filterBy-select-label">
                          Тип фильтра
                        </InputLabel>
                        <Select
                          labelId="filterBy-select-label"
                          id="filterBy-select"
                          value={value}
                          label="Тип фильтра"
                          onChange={(e) => {
                            onChange(e.target.value);
                            resetValue();
                          }}
                          size="small"
                        >
                          {filterBy.reduce<ReactNode[]>((prev, cur: string[]) => {
                            if (!filters.find((i) => i[0] === cur[1]))
                              return [
                                ...prev,
                                <MenuItem key={cur[0] + cur[1]} value={cur[1]}>
                                  {cur[0]}
                                </MenuItem>,
                              ];

                            return prev;
                          }, [])}
                        </Select>
                      </FormControl>
                    )}
                  />
                  {filterByWatch && (
                    <>
                      <Controller
                        control={control}
                        name="value"
                        render={({field: {onChange, value, ref}, fieldState: {error}}) => (
                          <>
                            {dictSearch && dictSearch[filterByWatch] ? (
                              <Autocomplete
                                key="dictSearch"
                                open={openSelect}
                                onOpen={() => {
                                  setOpenSelect(true);
                                }}
                                onClose={() => {
                                  setOpenSelect(false);
                                }}
                                onChange={(_, newValue) => {
                                  if (isNull(newValue) || !isObject(newValue)) return;
                                  setValue('value', newValue);
                                }}
                                value={value as ISelectOption}
                                getOptionLabel={(option) =>
                                  option && typeof option === 'object' ? (option as ISelectOption).label : ''
                                }
                                inputValue={searchTerm}
                                onInputChange={(_, newInputValue) => {
                                  handleSearch(newInputValue);
                                }}
                                options={data as ISelectOption[]}
                                loading={searchLoading}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    error={!!error}
                                    helperText={error ? error.message : ''}
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            ) : predefined && predefined[filterByWatch] ? (
                              <FormControl fullWidth>
                                <Select
                                  id="predefined-filter-value"
                                  value={value}
                                  onChange={onChange}
                                  size="small"
                                  disabled={
                                    predefined[filterByWatch] instanceof Promise ||
                                    predefined[filterByWatch] instanceof Error
                                  }
                                >
                                  {Array.isArray(predefined[filterByWatch]) ? (
                                    (predefined[filterByWatch] as string[][]).map((item) => (
                                      <MenuItem key={item[0] + item[1]} value={item[1]}>
                                        {item[0]}
                                      </MenuItem>
                                    ))
                                  ) : (
                                    <MenuItem key="another">
                                      {predefined[filterByWatch] instanceof Promise ? (
                                        <CircularProgress size="small" />
                                      ) : (
                                        'Ошибка'
                                      )}
                                    </MenuItem>
                                  )}
                                </Select>
                              </FormControl>
                            ) : isString(value) && !disableMemo ? (
                              <Autocomplete
                                key="memorized"
                                onChange={(_, newValue) => {
                                  setValue('value', newValue?.value ?? '');
                                }}
                                value={{title: value, value}}
                                getOptionLabel={(option) => option.title}
                                filterOptions={(options, params) => {
                                  const filtered = filter(options, params);
                                  const {inputValue: value} = params;
                                  const isExisting = options.some((option) => value === option.value);
                                  if (value !== '' && !isExisting) {
                                    filtered.push({
                                      value,
                                      title: value,
                                    });
                                  }

                                  return filtered;
                                }}
                                options={
                                  isPrevSelectedKey(filterByWatch)
                                    ? prevSelectedData[filterByWatch].map((i) => ({title: i, value: i}))
                                    : []
                                }
                                renderInput={(params) => {
                                  return (
                                    <TextField
                                      {...params}
                                      size="small"
                                      error={!!error}
                                      helperText={error ? error.message : ''}
                                    />
                                  );
                                }}
                              />
                            ) : (
                              <TextField
                                size="small"
                                fullWidth
                                onChange={onChange}
                                value={value}
                                inputRef={ref}
                                error={!!error}
                                helperText={error ? error.message : ''}
                                onKeyDown={keyHandler}
                              />
                            )}
                            <Stack sx={{justifyContent: 'flex-end', mt: 1}} direction="row" spacing={2}>
                              <Button
                                sx={{fontWeight: 'bold'}}
                                size="small"
                                color="error"
                                onClick={() => cancelFilter()}
                              >
                                Отмена
                              </Button>
                              <Button
                                sx={{fontWeight: 'bold'}}
                                size="small"
                                disabled={!value || !isValid}
                                onClick={handleSubmit(addFilter)}
                              >
                                Применить
                              </Button>
                            </Stack>
                          </>
                        )}
                      />
                    </>
                  )}
                </FiltersAccordion>
              </Box>

              <Box>
                <FiltersAccordion
                  ref={panel2Ref}
                  disabled={!filtersSets.length || !filtersSets.filter((i) => i.id !== currFiltersSet?.id).length}
                  summaryContent={currFiltersSet ? currFiltersSet.name : 'Наборы фильтров'}
                  isLoading={isLoading}
                >
                  {filtersSets.reduce<ReactNode[]>((prev, cur: IFiltersSet) => {
                    if (cur.id !== currFiltersSet?.id)
                      return [
                        ...prev,
                        <FiltersSetItem
                          closeAccordion={() => {
                            panel2Ref.current?.close();
                          }}
                          key={cur.id}
                          set={cur}
                          filtersSetsState={filtersSetsState}
                          filtersState={filtersState}
                          currentFiltersSetState={currentFiltersSetState}
                        />,
                      ];

                    return prev;
                  }, [])}
                </FiltersAccordion>
              </Box>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              sx={{height: '32px', alignItems: 'center', position: 'absolute', right: 0}}
            >
              <Button
                sx={{
                  ...buttonStyle,
                  fontSize: '14px',
                  borderRadius: 50,
                  height: '24px',
                  backgroundColor: blue[50],
                  '&:hover': {
                    backgroundColor: blue[100],
                  },
                  color: '#000',
                  border: `1px solid ${blue[100]}`,
                }}
                size="small"
                variant="contained"
                endIcon={<SaveIcon color={!filters.length ? 'disabled' : 'primary'} />}
                disabled={!filters.length}
                onClick={toggleOpenModal}
              >
                Сохранить набор
              </Button>
              <Button
                onClick={resetFilters}
                sx={{
                  ...buttonStyle,
                  fontSize: '14px',
                  borderRadius: 50,
                  height: '24px',
                  backgroundColor: blue[50],
                  '&:hover': {
                    backgroundColor: blue[100],
                  },
                  color: '#000',
                  border: `1px solid ${blue[100]}`,
                }}
                size="small"
                variant="contained"
                endIcon={<RestartAltIcon color={!filters.length ? 'disabled' : 'primary'} />}
                disabled={!filters.length}
              >
                Сбросить все
              </Button>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            flexWrap="wrap"
            sx={{
              '& > div': {
                margin: '5px',
              },
            }}
          >
            {!!filters.length &&
              filters.map((filter) => {
                const selected = predefined?.[filter[0]];
                const arraySelected = isArray(selected) ? selected : [];
                const predefinedLabelAsValue = arraySelected.find((i) => i[1] === filter[1])?.[0];

                return (
                  <FilterItem
                    key={`${filter[0]}:${filter[1]}`}
                    deletable
                    labelAsValue={
                      predefined && Array.isArray(predefined[filter[0]]) && dicts && dicts[filter[0]]?.get(filter[1])
                    }
                    predefinedLabelAsValue={predefinedLabelAsValue}
                    filterBy={filter[0]}
                    value={filter[1]}
                    filtersState={filtersState}
                  />
                );
              })}
          </Stack>
        </Stack>
      </Box>
      {openModal && (
        <ModalContainer
          sideEffect={handleSetStrategy}
          renderModal={(sideEffect, loading) => (
            <SaveSetModal
              filtersState={filtersState}
              currentFiltersSetState={currentFiltersSetState}
              innerEffect={false}
              open={openModal}
              handleSideEffect={sideEffect}
              loading={loading}
              handleClose={toggleOpenModal}
              set={currFiltersSet}
              filters={filters}
            />
          )}
        />
      )}
    </>
  );
};

export default memo(Filters);
